<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useClone } from '@/composables/object'
import { useReportColumnTemplates } from '@/composables/report-column-template'
import { useAccountingStore } from '@/stores/accounting.store'

import ColumnChecklist from '@/components/shared/ColumnChecklist.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  disable: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const i18n = getI18n()
const { reportColumnGroupEnum } = useAccountingStore()
const { enabledSections, isVisibleColumn } = useReportColumnTemplates()

const sections = computed(() => enabledSections(props.modelValue).filter(section => section.columns.length))
const selectedKeys = (section) => props.modelValue.sections[section.key]?.columns || []
const visibleColumns = (section) => section.columns.filter(isVisibleColumn).map(column => ({
  key: column.key,
  label: i18n.t(column.label),
  required: column.required,
  internal: column.group === reportColumnGroupEnum.INTERNAL,
}))
const visibleSelectedKeys = (section) => {
  const visible = visibleColumns(section).map(column => column.key)
  return selectedKeys(section).filter(key => visible.includes(key))
}

const updateSection = (section, mutate) => {
  const config = useClone(props.modelValue)
  mutate(config.sections[section.key])
  emit('update:modelValue', config)
}

const onColumnsChanged = (section, keys) => {
  const visible = visibleColumns(section).map(column => column.key)
  updateSection(section, (sectionConfig) => { sectionConfig.columns = [...keys, ...sectionConfig.columns.filter(key => !visible.includes(key))] })
}
</script>
<template>
  <div class="column q-gutter-y-sm">
    <q-expansion-item
      v-for="section in sections"
      :key="section.key"
      class="report-columns-section"
      header-class="text-weight-medium"
      :label="i18n.t(section.label)"
      :caption="i18n.t('common.report.columnsCount', selectedKeys(section).length)"
      default-opened
      dense
    >
      <column-checklist
        class="report-columns-list"
        :columns="visibleColumns(section)"
        :model-value="visibleSelectedKeys(section)"
        :disable="disable"
        @update:model-value="keys => onColumnsChanged(section, keys)"
      />
    </q-expansion-item>
  </div>
</template>
<style scoped lang="scss">
.report-columns-section {
  border: 1px solid $grey-4;
  border-radius: $generic-border-radius;
  overflow: hidden;
}

.report-columns-list {
  border-top: 1px solid $grey-4;
}
</style>
